import { MerchState, ProductSample } from './state';
import {
  CreateProductRequest,
  CreateProductResponse,
  CreateProductVariants,
} from '@services/merch/types/api/create-product';
import { UpdateProductRequest } from '@services/merch/types/api/update-product';
import {
  CreateMerchOrderRequest,
  MerchOrderVariant,
} from '@services/merch/types/api/create-merch-order';
import { OrderType } from '@webapp/utils/merch/constants';
import { MerchProductColor } from '@services/merch/types/api/merch-product-color';
import { MerchProductVariant } from '@services/merch/types/api/merch-product-variant';
import { MerchProductSize } from '@services/merch/types/api/merch-product-size';
import { MerchProduct } from '@services/merch/types/api/merch-product';

export const createMerchRequest = (
  state: MerchState
): CreateProductRequest | UpdateProductRequest => {
  return {
    imageId:
      (state.active?.image && state.active?.image[0]?.imageId) ||
      state.active?.editProduct?.printify[0].imageId,
    itemId:
      state.active?.product?.id ||
      state.active?.editProduct?.printify[0].itemId,
    position: state.active?.position,
    providerId: state.active?.colorSize?.providerId,
    sizes: state.active?.selectedSizes,
    colors:
      state.active?.selectedColors?.filter(
        (color) => color.toUpperCase() !== 'UNIQUE COLOR'
      ) || [],
    releaseId: state.active?.associatedRelease?.id,
    releaseName: state.active?.associatedRelease?.title,
    title:
      state.active?.productData?.productName ||
      state.active?.editProduct?.title ||
      state.active?.product?.title,
    price:
      Number(state.active?.productData?.productPrice) ||
      state.active?.editProduct?.price,
    qty: state.active?.quantity,
    unlimitedQty: state.active?.isUnlimited,
    images: state.active?.image
      ? state.active?.image
      : state.active?.editProduct?.printify.map((val) => {
          return {
            id: val.imageId,
            position: val.position,
            placeholder: val.placeholder,
          };
        }),
  } as CreateProductRequest | UpdateProductRequest;
};

export const getVariantsWithSize = ({
  variants,
  productSample,
}: {
  variants: CreateProductVariants[];
  productSample: ProductSample;
}): MerchOrderVariant[] => {
  if (variants.length === 1) {
    return [{ id: variants[0].id, quantity: 1 }];
  }
  const productVariants =
    productSample.productColors?.[0] === 'Unique Color'
      ? variants
          .filter((variant) => {
            return variant.size === productSample.productSize;
          })
          .slice(0, 1)
      : variants.filter(
          (variant) =>
            variant.size === productSample.productSize &&
            productSample.productColors?.includes(variant.color)
        );

  return productVariants.map((variant) => {
    return { id: variant.id, quantity: 1 };
  });
};

export const getMerchProductVariantsWithSize = ({
  variants,
  productSample,
  sizes,
  colors,
}: {
  variants: MerchProductVariant[];
  productSample: ProductSample;
  sizes: MerchProductSize[];
  colors: MerchProductColor[];
}): MerchOrderVariant[] => {
  if (variants.length === 1) {
    return [{ id: variants[0].id, quantity: 1 }];
  }
  const sizeIds = sizes
    .filter((size) => productSample.productSize === size.name)
    .map((size) => size.id);
  const colorIds = colors
    .filter((color) => productSample.productColors?.includes(color.name))
    .map((color) => color.id);
  const productVariants = colors.length
    ? variants.filter(
        (variant) =>
          variant.sizeId &&
          variant.colorId &&
          sizeIds.includes(variant.sizeId) &&
          colorIds.includes(variant.colorId)
      )
    : variants
        .filter((variant) => variant.sizeId && sizeIds.includes(variant.sizeId))
        .slice(0, 1);

  return productVariants.map((variant) => {
    return { id: variant.id, quantity: 1 };
  });
};

export const getVariantsWithColors = ({
  variants,
  productSample,
}: {
  variants: CreateProductVariants[];
  productSample: ProductSample;
}): MerchOrderVariant[] => {
  if (variants.length === 1) {
    return [{ id: variants[0].id, quantity: 1 }];
  }
  return variants
    .filter((variant) => productSample.productColors?.includes(variant.color))
    .map((variant) => {
      return { id: variant.id, quantity: 1 };
    });
};

export const getMerchProductVariantsWithColors = ({
  variants,
  productSample,
  colors,
}: {
  variants: MerchProductVariant[];
  productSample: ProductSample;
  colors: MerchProductColor[];
}): MerchOrderVariant[] => {
  if (variants.length === 1) {
    return [{ id: variants[0].id, quantity: 1 }];
  }

  const colorIds = colors
    .filter((color) => productSample.productColors?.includes(color.name))
    .map((color) => color.id);
  return variants
    .filter((variant) => variant.colorId && colorIds.includes(variant.colorId))
    .map((variant) => {
      return { id: variant.id, quantity: 1 };
    });
};

export const getProductSampleVariants = ({
  productSample,
  editProduct,
  newProduct,
}: {
  productSample: ProductSample;
  editProduct?: MerchProduct;
  newProduct?: CreateProductResponse;
}) => {
  if (productSample.productSize !== 'One size' && editProduct) {
    return getMerchProductVariantsWithSize({
      variants: editProduct.variants,
      productSample,
      sizes: editProduct.sizes,
      colors: editProduct.colors,
    });
  } else if (productSample.productSize !== 'One size' && newProduct) {
    return getVariantsWithSize({
      variants: newProduct.variants,
      productSample,
    });
  } else if (editProduct) {
    return getMerchProductVariantsWithColors({
      variants: editProduct.variants,
      productSample,
      colors: editProduct.colors,
    });
  } else if (newProduct) {
    return getVariantsWithColors({
      variants: newProduct.variants,
      productSample,
    });
  }
};

export const createMerchSampleOrderRequestBody = (
  productSample: ProductSample,
  productId: string,
  email?: string,
  phoneNumber?: string,
  editProduct?: MerchProduct,
  newProduct?: CreateProductResponse,
  artistId?: string
): CreateMerchOrderRequest => {
  const selectedVariants = getProductSampleVariants({
    productSample,
    editProduct,
    newProduct,
  });
  const [firstName, lastName] = productSample?.shippingDetails?.name.split(
    ' '
  ) || ['', ''];

  return {
    artistId,
    products: [
      {
        id: productId,
        variants: selectedVariants ?? [],
      },
    ],
    shippingMethod: productSample.shippingMethod,
    shippingCost: productSample.shippingCost,
    shippingNotification: true,
    shippingAddress: {
      first_name: firstName,
      last_name: lastName,
      address1: productSample.shippingDetails?.address || '',
      address2: '',
      city: productSample.shippingDetails?.city || '',
      region: productSample.shippingDetails?.region,
      zip: productSample.shippingDetails?.postCode || '',
      country: productSample.shippingDetails?.country || '',
      email: email,
      phone: phoneNumber,
    },
    orderType: OrderType.SAMPLE,
  };
};

export const createMerchShippingCostPayload = (
  productSample: ProductSample,
  productId: string,
  email: string,
  phoneNumber: string,
  editProduct?: MerchProduct,
  newProduct?: CreateProductResponse,
  artistId?: string
): CreateMerchOrderRequest | null => {
  if (!productSample.shippingDetails) {
    return null;
  }

  const selectedVariants = getProductSampleVariants({
    productSample,
    editProduct,
    newProduct,
  });
  const [firstName, lastName] =
    productSample.shippingDetails?.name.split(' ') || [];

  return {
    artistId,
    products: [
      {
        id: productId,
        variants: selectedVariants ?? [],
      },
    ],
    shippingAddress: {
      first_name: firstName,
      last_name: lastName,
      address1: productSample.shippingDetails.address,
      address2: '',
      city: productSample.shippingDetails.city,
      region: productSample.shippingDetails.region,
      zip: productSample.shippingDetails.postCode,
      country: productSample.shippingDetails.country,
      email: email,
      phone: phoneNumber,
    },
    orderType: OrderType.SAMPLE,
  };
};
